import { Navigate } from 'react-router-dom';
import { useFlag, useFeatureFlags } from '../hooks/useFeatureFlags';
import FeatureFlagsUnavailable from './FeatureFlagsUnavailable';
import type { FlagName } from '../types/feature-flags.generated';

interface FlaggedRouteProps {
  flag: FlagName;
  element: React.ReactElement;
}

/**
 * Route guard based on a feature flag.
 *
 * - While flags are not yet ready: renders null (prevents premature redirect
 *   or flashing the protected element before the client has loaded).
 *   Since task 705 "ready" means ready FOR THIS USER — a fetch has completed
 *   against a context carrying their identity. A cohort-gated flag evaluates
 *   false against the context-less first fetch, so acting on that answer
 *   redirected users who were inside the cohort. This branch now covers that
 *   window too, which is what lets `.page` flags carry role cohorts at all.
 * - Once ready: renders the element if the flag is enabled.
 * - Flag disabled while the flag system itself has failed
 *   (source === 'unleash-failed'): renders a stable "temporarily unavailable"
 *   panel instead of redirecting — a flag-system failure must never issue
 *   navigations, or the router can loop until the browser throttles the
 *   History API and React unmounts to a blank page (task 339). Still
 *   fail-closed on content: the protected element is not shown. With the
 *   type-aware fallback (task 340), operational flags resolve to their baked
 *   defaults during an outage, so this panel only appears for flags that
 *   resolve false while failed.
 * - Flag disabled on a healthy flag source: redirects to "/" with replace.
 */
export default function FlaggedRoute({ flag, element }: FlaggedRouteProps) {
  const { ready, source } = useFeatureFlags();
  const enabled = useFlag(flag);

  if (!ready) {
    return null;
  }

  if (enabled) {
    return element;
  }

  if (source === 'unleash-failed') {
    return <FeatureFlagsUnavailable />;
  }

  return <Navigate to="/" replace />;
}
